import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { DriverReferralLinkService } from '../../services/driver-referral-link.service';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ControlsOf } from '../../utils/controls-of';
import {
  SaveDriverReferralLinkSettingDTO,
  DriverReferralLinkSettingDTO,
} from '../../interfaces/driver-referral-link-setting';
import { BBWTMessageService } from '../../services';
import { flatMap } from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';

interface TemplateVariable {
  name: string;
  description: string;
}

interface FormLayoutControl {
  name: keyof SaveDriverReferralLinkSettingDTO;
  type: string | null;
  min?: number | null;
  max?: number | null;
  onChange?: (value: any) => void;
}

interface FormLayoutGroup {
  controls: FormLayoutControl[];
}

@Component({
  selector: 'driver-referral-link-admin',
  templateUrl: './driver-referral-link-admin.component.html',
  styleUrls: ['./driver-referral-link-admin.component.scss'],
})
export class DriverReferralLinkAdminComponent implements OnInit, OnDestroy, AfterViewInit {
  public readonly controlLabels: Record<keyof SaveDriverReferralLinkSettingDTO, string> = {
    linkUrlTemplate: 'Link URL Template',
    shareLinkMessageTemplate: 'Share Message Template',
    firstBonusNumberOfMoves: 'First Bonus Moves Required',
    firstBonusPounds: 'First Bonus (£)',
    secondBonusNumberOfMoves: 'Second Bonus Moves Required',
    secondBonusPounds: 'Second Bonus (£)',
    thirdBonusNumberOfMoves: 'Third Bonus Moves Required',
    thirdBonusPounds: 'Third Bonus (£)',
  };

  public readonly controlTooltips: Record<keyof SaveDriverReferralLinkSettingDTO, string> = {
    linkUrlTemplate:
      'Template for registration URL unique to referring user. Can use Template Variables',
    shareLinkMessageTemplate:
      'Template for pre-filled message content when a driver shares their link via WhatsApp, Email etc. Can use Template Variables',
    firstBonusNumberOfMoves:
      'The number of moves the referred driver must complete for the referring driver to receive the first bonus',
    firstBonusPounds:
      'The bonus amount in £ the referring driver receives after the referred driver completes the number of moves specified above',
    secondBonusNumberOfMoves:
      'The number of moves the referred driver must complete for the referring driver to receive the second bonus',
    secondBonusPounds:
      'The bonus amount in £ the referring driver receives after the referred driver completes the number of moves specified above',
    thirdBonusNumberOfMoves:
      'The number of moves the referred driver must complete for the referring driver to receive the third bonus',
    thirdBonusPounds:
      'The bonus amount in £ the referring driver receives after the referred driver completes the number of moves specified above',
  };

  public readonly formLayout: FormLayoutGroup[] = [
    {
      controls: [
        { name: 'linkUrlTemplate', type: 'text' },
        { name: 'shareLinkMessageTemplate', type: 'text' },
      ],
    },
    {
      controls: [
        {
          name: 'firstBonusNumberOfMoves',
          type: 'number',
          min: 1,
          onChange: () => {
            this.settingsForm.controls.secondBonusNumberOfMoves.updateValueAndValidity();
            this.settingsForm.controls.thirdBonusNumberOfMoves.updateValueAndValidity();
          },
        },
        { name: 'firstBonusPounds', type: 'number', min: 1 },
      ],
    },
    {
      controls: [
        {
          name: 'secondBonusNumberOfMoves',
          type: 'number',
          min: 1,
          onChange: () => {
            this.settingsForm.controls.thirdBonusNumberOfMoves.updateValueAndValidity();
          },
        },
        { name: 'secondBonusPounds', type: 'number', min: 1 },
      ],
    },
    {
      controls: [
        { name: 'thirdBonusNumberOfMoves', type: 'number', min: 1 },
        { name: 'thirdBonusPounds', type: 'number', min: 1 },
      ],
    },
  ];

  public templateVariables: TemplateVariable[] = [
    { name: '{{ Id }}', description: 'Id of the referring user' },
    { name: '{{ FirstName }}', description: 'First Name of the referring user' },
    { name: '{{ LastName }}', description: 'Last Name of the referring user' },
    { name: '{{ Email }}', description: 'Email of the referring user' },
    {
      name: '{{ OrganisationId }}',
      description: 'Id of the Organisation the referring user belongs to',
    },
    {
      name: '{{ Organisation.Name }}',
      description: 'Name of Organisation the referring user belongs to',
    },
    {
      name: '{{ ReferralLink }}',
      description: 'Generated Referral URL (Only available for Share Message Template)',
    },
  ];

  public currentSettings: DriverReferralLinkSettingDTO | null;

  constructor(
    private messageService: BBWTMessageService,
    private driverReferralLinkService: DriverReferralLinkService,
  ) {}

  loading: boolean = false;

  public get saveDisabledReason(): string | null {
    if (this.settingsForm.invalid) {
      return 'Form is invalid';
    }

    if (!this.settingsForm.dirty) {
      return 'Form has not been modified';
    }

    if (this.loading) {
      return 'Saving in progress';
    }

    return null;
  }

  settingsForm: FormGroup<ControlsOf<SaveDriverReferralLinkSettingDTO>> = new FormGroup<
    ControlsOf<SaveDriverReferralLinkSettingDTO>
  >({
    linkUrlTemplate: new FormControl<string>('', [Validators.required, Validators.maxLength(2000)]),
    shareLinkMessageTemplate: new FormControl<string>(
      'Register with Engineius using my unique link {{ ReferralLink }}',
      [Validators.required, Validators.maxLength(2000)],
    ),
    firstBonusNumberOfMoves: new FormControl<number>(0, Validators.min(1)),
    firstBonusPounds: new FormControl<number>(0, Validators.min(1)),
    secondBonusNumberOfMoves: new FormControl<number>(0, [
      Validators.min(1),
      additionalBonusNumberOfMovesValidator(this, 'firstBonusNumberOfMoves'),
    ]),
    secondBonusPounds: new FormControl<number>(0, Validators.min(1)),
    thirdBonusNumberOfMoves: new FormControl<number>(0, [
      Validators.min(1),
      additionalBonusNumberOfMovesValidator(this, 'secondBonusNumberOfMoves'),
    ]),
    thirdBonusPounds: new FormControl<number>(0, Validators.min(1)),
  });

  public get formErrors(): string[] {
    const controlKeys = Object.keys(this.settingsForm.controls);
    return flatMap(controlKeys, (key) =>
      getFormControlErrorMessages(
        this.controlLabels[key] || key,
        this.settingsForm.controls[key].errors,
      ),
    );
  }

  async ngOnInit() {
    void this.getCurrentSettings();
  }

  private onSettingsRetrieved(settings: DriverReferralLinkSettingDTO) {
    if (!settings) {
      return;
    }

    this.currentSettings = settings;
    this.settingsForm.patchValue(settings);
  }

  ngOnDestroy(): void {}

  async ngAfterViewInit() {}

  async getCurrentSettings(): Promise<void> {
    let settings: DriverReferralLinkSettingDTO | null = null;

    try {
      this.loading = true;
      settings = await this.driverReferralLinkService.getCurrentSettings();
    } catch (error) {
      this.messageService.ShowError(
        'Error',
        'Could not load existing driver referral link settings',
      );
      return null;
    } finally {
      this.loading = false;
    }

    this.onSettingsRetrieved(settings);
  }

  public async saveSettings() {
    if (this.settingsForm.invalid) {
      return;
    }

    this.loading = true;

    const dto = this.settingsForm.getRawValue();

    let settings: DriverReferralLinkSettingDTO | null = null;

    try {
      settings = await this.driverReferralLinkService.updateSettings(dto);
      this.messageService.ShowSuccess('Success', 'Driver referral link settings saved');
    } catch (error) {
      this.handleSaveError(error);
    } finally {
      this.loading = false;
    }

    this.onSettingsRetrieved(settings);
  }

  private handleSaveError(error: any) {
    console.error('Error saving driver referral link settings:', error);

    const isBadRequest = error instanceof HttpErrorResponse && error.status == 400;

    if (!isBadRequest) {
      this.messageService.ShowError(
        'Unknown Error',
        'Could not save driver referral link settings',
      );
      return;
    }

    const errorResponse = error.error;

    try {
      Object.keys(errorResponse).forEach((key) => {
        const control = this.settingsForm.get(key);

        if (control) {
          control.setErrors({
            ...control.errors,
            serverError: errorResponse[key],
          });

          control.markAsTouched();
        }
      });
    } catch (error) {
      console.error('Error processing server errors:', error);
    }
  }
}

function additionalBonusNumberOfMovesValidator(
  component: DriverReferralLinkAdminComponent,
  previousControlName: keyof SaveDriverReferralLinkSettingDTO,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const additionalBonusNumberOfMovesValue = Number(control.value);

    if (!additionalBonusNumberOfMovesValue) {
      return null;
    }

    const previousBonusNumberOfMovesValue = Number(
      component.settingsForm.getRawValue()[previousControlName],
    );

    if (!previousBonusNumberOfMovesValue) {
      return null;
    }

    if (additionalBonusNumberOfMovesValue > previousBonusNumberOfMovesValue) {
      return null;
    }

    return {
      additionalBonusNumberOfMoves: {
        min: previousBonusNumberOfMovesValue,
        previousControlLabel: component.controlLabels[previousControlName],
      },
    };
  };
}

function getFormControlErrorMessages(
  controlName: string,
  controlErrors: ValidationErrors,
): string[] {
  const errors = [];
  if (controlErrors == null) {
    return [];
  }

  if (controlErrors.required) {
    errors.push(`<b>${controlName}</b> is required`);
  }

  if (controlErrors.min) {
    errors.push(
      `<b>${controlName}</b> must be greater than or equal to <b>${controlErrors.min.min}</b>`,
    );
  }

  if (controlErrors.max) {
    errors.push(
      `<b>${controlName}</b> must be less than or equal to <b>${controlErrors.max.max}</b>`,
    );
  }

  if (controlErrors.maxLength) {
    errors.push(
      `<b>${controlName}</b> must be less than or equal to ${controlErrors.maxLength.requiredLength} characters`,
    );
  }

  if (controlErrors.additionalBonusNumberOfMoves) {
    errors.push(
      `<b>${controlName}</b> must be greater than <b>${controlErrors.additionalBonusNumberOfMoves.min}</b>`,
    );
  }
  if (controlErrors.serverError) {
    errors.push(
      ...controlErrors.serverError.map(
        (e) => `<b>${controlName}</b> - ${e.replace('Error - ', '')}`,
      ),
    );
  }

  return errors;
}
